// src/features/blog/hooks/useBlog.js
import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchBlogPosts,
  fetchBlogPostById,
  fetchBlogStats,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
  publishBlogPost,
  uploadBlogImage,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
} from "../../../app/slice/blogSlice";

const selectBlogState = (state) => state.blog || {};
const selectPosts = (state) => selectBlogState(state).posts || [];
const selectSelectedPost = (state) =>
  selectBlogState(state).selectedPost || null;
const selectLoading = (state) => selectBlogState(state).loading || false;
const selectError = (state) => selectBlogState(state).error || null;
const selectSuccess = (state) => selectBlogState(state).success || null;
const selectFilters = (state) =>
  selectBlogState(state).filters || {
    status: "all",
    category: "all",
    dateRange: { start: null, end: null },
    authorId: "all",
    search: "",
    tags: [],
  };
const selectStats = (state) =>
  selectBlogState(state).stats || {
    total: 0,
    draft: 0,
    published: 0,
    scheduled: 0,
    archived: 0,
    pending_review: 0,
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    averageViews: 0,
    averageLikes: 0,
    averageComments: 0,
  };

export const useBlog = () => {
  const dispatch = useDispatch();

  const posts = useSelector(selectPosts);
  const selectedPost = useSelector(selectSelectedPost);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const success = useSelector(selectSuccess);
  const filters = useSelector(selectFilters);
  const stats = useSelector(selectStats);

  const getPosts = useCallback(
    (filterParams = {}) => {
      dispatch(fetchBlogPosts({ filters: { ...filters, ...filterParams } }));
    },
    [dispatch, filters],
  );

  const getPostById = useCallback(
    (id) => {
      dispatch(fetchBlogPostById(id));
    },
    [dispatch],
  );

  const getStats = useCallback(() => {
    dispatch(fetchBlogStats());
  }, [dispatch]);

  const createNewPost = useCallback(
    async (data) => {
      const result = await dispatch(createBlogPost(data));
      return result;
    },
    [dispatch],
  );

  const updateExistingPost = useCallback(
    async (id, data) => {
      const result = await dispatch(updateBlogPost({ id, data }));
      return result;
    },
    [dispatch],
  );

  const deleteExistingPost = useCallback(
    async (id) => {
      const result = await dispatch(deleteBlogPost(id));
      return result;
    },
    [dispatch],
  );

  const publishExistingPost = useCallback(
    async (id) => {
      const result = await dispatch(publishBlogPost(id));
      return result;
    },
    [dispatch],
  );

  const uploadImage = useCallback(
    async ({ file, path = "blog/" }) => {
      const result = await dispatch(uploadBlogImage({ file, path }));
      return result.payload;
    },
    [dispatch],
  );

  const updateFilters = useCallback(
    (newFilters) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  const clearAllFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  const clearErrorMessage = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearSuccessMessage = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  useEffect(() => {
    getPosts();
    getStats();
  }, []);

  useEffect(() => {
    getPosts();
  }, [filters]);

  return {
    posts,
    selectedPost,
    loading,
    error,
    success,
    filters,
    stats,
    getPosts,
    getPostById,
    getStats,
    createNewPost,
    updateExistingPost,
    deleteExistingPost,
    publishExistingPost,
    uploadImage,
    updateFilters,
    clearAllFilters,
    clearErrorMessage,
    clearSuccessMessage,
  };
};

export default useBlog;
